import { Component, signal, computed, effect, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { Header } from "../../header/header";
import { Footer } from "../../footer/footer";
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../services/auth.service';
import { Router } from '@angular/router';

// Interfaces para type safety
interface ContactInformation {
  nameContactType: string;
  desContactInformation: string;
}

interface PhotoPerson {
  LinkFile: string;
}

interface Program {
  nameProgram: string;
}

interface UserProfile {
  lastNamePerson: string;
  namPerson: string;
  information: ContactInformation[];
  photoPerson: PhotoPerson[];
  programs: Program[];
  documentNumber?: string;
}

interface ProfileResponse {
  [index: number]: UserProfile;
}

interface ProfileData {
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  cellPhone: string;
  avatar: string;
  program: string;
  documentNumber: string;
}

@Component({
  selector: 'app-profile',
  imports: [Header, Footer],
  templateUrl: './profile.html',
  styleUrl: './profile.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class Profile {
  // Dependency injection con inject()
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  // Constants
  private readonly idIbero = environment.idIbero;
  private readonly idIberoVirtual = environment.idIberoVirtual;
  private readonly API_URL = 'https://as-ws-zona-transacional.azurewebsites.net/api/ibero/ListPersonByEmail';

  // Signals para state management
  public userProfile = signal<UserProfile | null>(null);
  public profileData = signal<ProfileData>({
    fullName: '',
    firstName: '',
    lastName: '',
    email: '',
    cellPhone: 'No disponible',
    avatar: '',
    program: 'No especificado',
    documentNumber: ''
  });
  
  public gsid = signal<string | null>(localStorage.getItem('gsid'));
  public email = signal<string | null>(localStorage.getItem('gsemail'));
  public isLoading = signal<boolean>(false);
  public error = signal<string | null>(null);

  // Computed signals
  public isIberoUser = computed(() => {
    const currentGsid = this.gsid();
    return currentGsid === this.idIbero || currentGsid === this.idIberoVirtual;
  });

  public hasProfile = computed(() => this.userProfile() !== null);
  public hasAvatar = computed(() => !!this.profileData().avatar);
  public hasProgram = computed(() => this.profileData().program !== 'No especificado');
  
  public initials = computed(() => {
    const data = this.profileData();
    const first = data.firstName.charAt(0).toUpperCase();
    const last = data.lastName.charAt(0).toUpperCase();
    return `${first}${last}`;
  });

  public displayName = computed(() => {
    const data = this.profileData();
    return `${data.lastName} ${data.firstName}`.trim();
  });

  // Effect para cargar perfil automáticamente
  constructor() {
    effect(() => {
      if (this.isIberoUser() && this.email()) {
        this.loadIberoProfile();
      } else if (!this.isIberoUser()) {
        console.warn('No es un usuario Ibero, se debe ejecutar otro método de carga de perfil para los usuarios de Zyon');
      }
    });
  }

  /**
   * Carga el perfil del usuario desde la API de Ibero
   */
  private loadIberoProfile(): void {
    const emailValue = this.email();

    if (!emailValue) {
      const errorMsg = 'No se encontró el correo electrónico en el almacenamiento local';
      console.error(errorMsg, this.gsid());
      this.error.set(errorMsg);
      return;
    }

    this.isLoading.set(true);
    this.error.set(null);

    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    this.http.get<ProfileResponse>(
      `${this.API_URL}/${emailValue}`,
      { headers }
    ).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (response) => {
        if (response && response[0]) {
          console.log('Profile loaded successfully:', response[0]);
          this.userProfile.set(response[0]);
          this.parseProfileData(response[0], emailValue);
        } else {
          this.error.set('No se encontró información del perfil');
        }
        this.isLoading.set(false);
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading.set(false);
        this.handleProfileError(error);
      }
    });
  }

  /**
   * Parsea los datos del perfil a un formato más manejable
   */
  private parseProfileData(profile: UserProfile, email: string): void {
    const cellPhoneInfo = profile.information?.find(
      (info) => info.nameContactType === 'Teléfono Celular'
    );

    this.profileData.set({
      fullName: `${profile.lastNamePerson} ${profile.namPerson}`.trim(),
      firstName: profile.namPerson || '',
      lastName: profile.lastNamePerson || '',
      email: email,
      cellPhone: cellPhoneInfo?.desContactInformation || 'No disponible',
      avatar: profile.photoPerson?.[0]?.LinkFile || '',
      program: profile.programs?.[0]?.nameProgram || 'No especificado',
      documentNumber: profile.documentNumber || ''
    });
  }

  /**
   * Maneja errores de carga del perfil
   */
  private handleProfileError(error: HttpErrorResponse): void {
    let errorMessage = 'Error al cargar el perfil';

    switch (error.status) {
      case 401:
        errorMessage = 'Sesión expirada. Por favor, inicie sesión nuevamente';
        console.error('Sesión expirada o token inválido');
        // Dar tiempo para mostrar el mensaje antes de cerrar sesión
        setTimeout(() => this.authService.logout(), 2000);
        break;
      case 404:
        errorMessage = 'No se encontró el perfil del usuario';
        break;
      case 500:
        errorMessage = 'Error del servidor. Por favor, intente más tarde';
        break;
      default:
        errorMessage = `Error al cargar el perfil: ${error.message}`;
    }

    console.error('Error loading profile:', error);
    this.error.set(errorMessage);
  }

  /**
   * Cierra la sesión del usuario
   */
  public logout(): void {
    const redirectUri = this.gsid();
    this.authService.logout();
    console.log('Session closed, redirecting to:', redirectUri);
    this.router.navigate([`/${redirectUri || 'login'}`]);
  }

  /**
   * Reintenta cargar el perfil
   */
  public retryLoadProfile(): void {
    this.error.set(null);
    this.loadIberoProfile();
  }

  /**
   * Limpia el mensaje de error
   */
  public clearError(): void {
    this.error.set(null);
  }

  /**
   * Obtiene el avatar por defecto basado en las iniciales
   */
  public getDefaultAvatar(): string {
    return `data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'><rect fill='%23ddd' width='100' height='100'/><text x='50' y='50' text-anchor='middle' dominant-baseline='middle' font-size='40' fill='%23999'>${this.initials()}</text></svg>`;
  }

  /**
   * Navega a la página de billetera
   */
  public navigateToWallet(): void {
    this.router.navigate(['/wallet']);
  }

  /**
   * Navega a la página de términos y condiciones
   */
  public navigateToTerms(): void {
    this.router.navigate(['/terms']);
  }

  /**
   * Navega a la página de edición de perfil
   */
  public navigateToEditProfile(): void {
    this.router.navigate(['/edit-profile']);
  }
}